import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';

// ── Content moderation ────────────────────────────────────────────────────────
const BLOCKED_PATTERNS = [
  /\bspam\b/i,
  /click here/i,
  /\bdm me\b/i,
  /\bfollow me\b/i,
  /get rich quick/i,
  /guaranteed (returns?|profits?|income)/i,
  /\b(shit|fuck|cunt|asshole|bitch|bastard|retard)\b/i,
  /\b(idiot|moron|you('re| are) (stupid|dumb|an? idiot))\b/i,
  /knowfi (sucks?|is (trash|garbage|scam|fake|fraud|terrible|awful|horrible|useless))/i,
  /\b(telegram|whatsapp|signal)\s*:\s*[@+]/i,
  /(https?:\/\/(?!knowfi\.io)[^\s]{10,})/i,
  /\bwire (me|transfer)\b/i,
  /make \$[\d,]+ (per|a) (day|week|month)/i,
  /\bpyramid scheme\b/i,
  /\b(invest with me|join my (group|channel|discord))\b/i,
  /\b(promo code|referral link|affiliate)\b/i,
  /send (me|your) (money|crypto|btc|eth)\b/i,
];

function isFlagged(text) {
  return BLOCKED_PATTERNS.some(re => re.test(text));
}

const MIN_POST_CHARS = 20;
const MAX_POSTS_PER_HOUR = 5;
const RATE_KEY = 'knowfi_community_rate';

function checkRateLimit() {
  try {
    const now = Date.now();
    const raw = localStorage.getItem(RATE_KEY);
    const ts = raw ? JSON.parse(raw).filter(t => now - t < 3600000) : [];
    if (ts.length >= MAX_POSTS_PER_HOUR) return false;
    ts.push(now);
    localStorage.setItem(RATE_KEY, JSON.stringify(ts));
    return true;
  } catch { return true; }
}

const EMAIL_GATE_KEY  = 'knowfi_community_email';
const STORAGE_KEY     = 'knowfi_community_posts_v2';
const VOTES_KEY       = 'knowfi_community_votes';
const MY_POSTS_KEY    = 'knowfi_community_my_posts';

const CATEGORIES = [
  { id: 'all',       label: 'All',           emoji: '📋' },
  { id: 'wins',      label: 'Money Wins',    emoji: '🎉' },
  { id: 'questions', label: 'Questions',     emoji: '❓' },
  { id: 'tips',      label: 'Tips',          emoji: '💡' },
  { id: 'check',     label: 'Check My Plan', emoji: '📊' },
];

const CAT_STYLES = {
  wins:      { bg: 'rgba(34,197,94,0.12)',   color: '#4ade80', border: 'rgba(34,197,94,0.25)'   },
  questions: { bg: 'rgba(56,189,248,0.12)',  color: '#38bdf8', border: 'rgba(56,189,248,0.25)'  },
  tips:      { bg: 'rgba(245,158,11,0.12)',  color: '#f59e0b', border: 'rgba(245,158,11,0.25)'  },
  check:     { bg: 'rgba(167,139,250,0.12)', color: '#a78bfa', border: 'rgba(167,139,250,0.25)' },
};

const SEED_POSTS = [
  {
    id: 'seed-1', category: 'wins',
    text: 'Just paid off my last credit card — $8,200 gone forever!! Used the Game Plan to stay on track 🙌',
    amount: '$8,200', anonymous: false, isPro: true, authorName: 'JessicaM',
    timestamp: new Date(Date.now() - 2 * 3600000).toISOString(), upvotes: 47,
    replies: [
      { id: 'r-1a', text: 'That is HUGE! How long did it take you?', anonymous: true, isPro: false, authorName: 'Anonymous 🦆', timestamp: new Date(Date.now() - 1.5 * 3600000).toISOString(), upvotes: 5 },
      { id: 'r-1b', text: 'Congrats!! I just started my debt payoff journey — this is so motivating 🙌', anonymous: false, isPro: false, authorName: 'TylerK', timestamp: new Date(Date.now() - 1 * 3600000).toISOString(), upvotes: 12 },
    ],
  },
  {
    id: 'seed-2', category: 'questions',
    text: "Is a Roth IRA better than a 401k if my employer doesn't match? 26 years old.",
    amount: '', anonymous: true, isPro: false, authorName: 'Anonymous 🦆',
    timestamp: new Date(Date.now() - 5 * 3600000).toISOString(), upvotes: 31,
    replies: [
      { id: 'r-2a', text: 'Generally yes — Roth IRA gives you more control and tax-free growth. Max it out ($7,000/yr) before worrying about an unmatched 401k.', anonymous: false, isPro: true, authorName: 'MarcusR', timestamp: new Date(Date.now() - 4 * 3600000).toISOString(), upvotes: 18 },
    ],
  },
  {
    id: 'seed-3', category: 'wins',
    text: 'Hit $10k in my emergency fund for the first time in my life. 34 years old and finally feel safe.',
    amount: '$10,000', anonymous: false, isPro: false, authorName: 'ChrisT',
    timestamp: new Date(Date.now() - 86400000).toISOString(), upvotes: 89,
    replies: [],
  },
  {
    id: 'seed-4', category: 'tips',
    text: 'Set up automatic transfers the day after payday so you literally never see the money. Saved $300/mo without feeling it.',
    amount: '', anonymous: false, isPro: true, authorName: 'SarahL',
    timestamp: new Date(Date.now() - 2 * 86400000).toISOString(), upvotes: 124,
    replies: [
      { id: 'r-4a', text: 'This is genuinely the best advice. I did this 6 months ago and have $1,800 saved I would have 100% spent.', anonymous: false, isPro: false, authorName: 'DerekW', timestamp: new Date(Date.now() - 1.5 * 86400000).toISOString(), upvotes: 33 },
    ],
  },
  {
    id: 'seed-5', category: 'questions',
    text: "What's everyone's take on I-bonds right now? Worth it with current rates?",
    amount: '', anonymous: true, isPro: false, authorName: 'Anonymous 🦆',
    timestamp: new Date(Date.now() - 3 * 86400000).toISOString(), upvotes: 22,
    replies: [],
  },
  {
    id: 'seed-6', category: 'wins',
    text: "Started investing at 19 — just hit $1,000 in my Roth IRA. I know it's small but I'm proud!",
    amount: '$1,000', anonymous: false, isPro: false, authorName: 'AlexP',
    timestamp: new Date(Date.now() - 4 * 86400000).toISOString(), upvotes: 203,
    replies: [
      { id: 'r-6a', text: "It's NOT small. At 19 that $1,000 could be $15k+ by retirement with compound interest. You're winning.", anonymous: false, isPro: true, authorName: 'NatalieF', timestamp: new Date(Date.now() - 3.5 * 86400000).toISOString(), upvotes: 44 },
    ],
  },
  {
    id: 'seed-7', category: 'check',
    text: 'Running the retirement calculator scared me into finally starting my 401k. Sometimes you need the fear.',
    amount: '', anonymous: false, isPro: false, authorName: 'BrandonH',
    timestamp: new Date(Date.now() - 5 * 86400000).toISOString(), upvotes: 67,
    replies: [],
  },
  {
    id: 'seed-8', category: 'tips',
    text: 'The KnowFi Brief finally explained why Fed rate changes actually matter to my savings account rate. Hadn\'t understood that connection before.',
    amount: '', anonymous: false, isPro: true, authorName: 'PriyaS',
    timestamp: new Date(Date.now() - 6 * 86400000).toISOString(), upvotes: 88,
    replies: [],
  },
];

function formatTimeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  const hrs  = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 2)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs  < 24) return `${hrs}h ago`;
  return `${days}d ago`;
}

function loadMyPosts() { try { const r = localStorage.getItem(MY_POSTS_KEY); return r ? JSON.parse(r) : []; } catch { return []; } }
function saveMyPosts(ids) { localStorage.setItem(MY_POSTS_KEY, JSON.stringify(ids)); }
function loadPosts() {
  try { const r = localStorage.getItem(STORAGE_KEY); if (!r) return SEED_POSTS; return JSON.parse(r).map(p => ({ ...p, replies: p.replies || [] })); }
  catch { return SEED_POSTS; }
}
function savePosts(posts) { localStorage.setItem(STORAGE_KEY, JSON.stringify(posts)); }
function loadVotes() { try { const r = localStorage.getItem(VOTES_KEY); return r ? JSON.parse(r) : {}; } catch { return {}; } }
function saveVotes(v) { localStorage.setItem(VOTES_KEY, JSON.stringify(v)); }

function ProBadge() {
  return (
    <span style={{
      background: 'linear-gradient(135deg, #f59e0b, #f97316)',
      color: '#0f172a', fontSize: '9px', fontWeight: '900',
      padding: '2px 6px', borderRadius: '4px', letterSpacing: '0.8px',
      verticalAlign: 'middle', marginLeft: '5px', userSelect: 'none',
    }}>PRO</span>
  );
}

export default function Community() {
  const [posts, setPosts]                 = useState([]);
  const [votes, setVotes]                 = useState({});
  const [activeTab, setActiveTab]         = useState('all');
  const [showModal, setShowModal]         = useState(false);
  const [form, setForm]                   = useState({ category: 'wins', text: '', amount: '', anonymous: false });
  const [submitError, setSubmitError]     = useState('');
  const [replyOpen, setReplyOpen]         = useState(null);
  const [replyText, setReplyText]         = useState('');
  const [replyAnon, setReplyAnon]         = useState(false);
  const [replyError, setReplyError]       = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [myPosts, setMyPosts]             = useState([]);
  const [gatedEmail, setGatedEmail]       = useState('');
  const [emailDraft, setEmailDraft]       = useState('');
  const [emailError, setEmailError]       = useState('');
  const [emailStep, setEmailStep]         = useState(false);
  const [currentUser, setCurrentUser]     = useState(null);

  useEffect(() => {
    setPosts(loadPosts());
    setVotes(loadVotes());
    setMyPosts(loadMyPosts());
    const loggedIn = (() => { try { const u = localStorage.getItem('knowfi_user'); return u ? JSON.parse(u) : null; } catch { return null; } })();
    if (loggedIn?.email) { setGatedEmail(loggedIn.email); setCurrentUser(loggedIn); }
    else { const saved = localStorage.getItem(EMAIL_GATE_KEY); if (saved) setGatedEmail(saved); }
  }, []);

  const filtered = activeTab === 'all' ? posts : posts.filter(p => p.category === activeTab);

  const handleUpvote = (id, isReply, postId) => {
    const key = isReply ? `reply-${id}` : id;
    const alreadyVoted = !!votes[key];
    const delta = alreadyVoted ? -1 : 1;
    const newVotes = { ...votes, [key]: !alreadyVoted };
    let newPosts;
    if (isReply) {
      newPosts = posts.map(p =>
        p.id === postId
          ? { ...p, replies: p.replies.map(r => r.id === id ? { ...r, upvotes: Math.max(0, (r.upvotes || 0) + delta) } : r) }
          : p
      );
    } else {
      newPosts = posts.map(p => p.id === id ? { ...p, upvotes: Math.max(0, p.upvotes + delta) } : p);
    }
    setVotes(newVotes); setPosts(newPosts);
    saveVotes(newVotes); savePosts(newPosts);
  };

  const openPostModal = () => {
    if (!gatedEmail) { setEmailStep(true); return; }
    setShowModal(true);
  };

  const handleEmailGate = (e) => {
    e.preventDefault();
    const trimmed = emailDraft.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError('Please enter a valid email address.'); return;
    }
    localStorage.setItem(EMAIL_GATE_KEY, trimmed);
    setGatedEmail(trimmed);
    setEmailStep(false); setEmailDraft(''); setEmailError('');
    const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    fetch(`${API}/api/email-subscribe`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: trimmed, source: 'community-gate' }),
    }).catch(() => {});
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = form.text.trim();
    if (!text) { setSubmitError('Please write something before posting.'); return; }
    if (text.length < MIN_POST_CHARS) { setSubmitError(`Posts must be at least ${MIN_POST_CHARS} characters.`); return; }
    if (isFlagged(text)) {
      setSubmitError('Your post was flagged by our filter. Please remove any spam, external links, or prohibited language.');
      return;
    }
    if (!checkRateLimit()) {
      setSubmitError(`You've reached the limit of ${MAX_POSTS_PER_HOUR} posts per hour. Please wait before posting again.`);
      return;
    }
    const isPro = !form.anonymous && currentUser?.plan === 'pro';
    const authorName = form.anonymous ? 'Anonymous 🦆' : (currentUser?.name || 'KnowFi Member');
    const newPost = {
      id: `post-${Date.now()}`, category: form.category,
      text, amount: form.amount.trim(),
      anonymous: form.anonymous, isPro,
      authorName,
      timestamp: new Date().toISOString(),
      upvotes: 0, replies: [],
    };
    const newPosts = [newPost, ...posts];
    const newMyPosts = [...myPosts, newPost.id];
    setPosts(newPosts); savePosts(newPosts);
    setMyPosts(newMyPosts); saveMyPosts(newMyPosts);
    setShowModal(false);
    setForm({ category: 'wins', text: '', amount: '', anonymous: false });
    setSubmitError('');
  };

  const toggleReply = (postId) => {
    if (!gatedEmail) { setEmailStep(true); return; }
    if (replyOpen === postId) { setReplyOpen(null); setReplyText(''); setReplyError(''); }
    else { setReplyOpen(postId); setReplyText(''); setReplyError(''); }
  };

  const handleReplySubmit = (postId) => {
    const text = replyText.trim();
    if (!text) { setReplyError('Reply cannot be empty.'); return; }
    if (text.length < 5) { setReplyError('Reply is too short.'); return; }
    if (isFlagged(text)) {
      setReplyError('Reply flagged. Please remove any spam, links, or prohibited language.'); return;
    }
    const isPro = !replyAnon && currentUser?.plan === 'pro';
    const authorName = replyAnon ? 'Anonymous 🦆' : (currentUser?.name || 'KnowFi Member');
    const newReply = {
      id: `reply-${Date.now()}`, text,
      anonymous: replyAnon, isPro, authorName,
      timestamp: new Date().toISOString(), upvotes: 0,
    };
    const newPosts = posts.map(p => p.id === postId ? { ...p, replies: [...(p.replies || []), newReply] } : p);
    const newMyPosts = [...myPosts, newReply.id];
    setPosts(newPosts); savePosts(newPosts);
    setMyPosts(newMyPosts); saveMyPosts(newMyPosts);
    setReplyOpen(null); setReplyText(''); setReplyAnon(false); setReplyError('');
  };

  const handleDelete = () => {
    if (!confirmDelete) return;
    let newPosts;
    if (confirmDelete.type === 'post') {
      newPosts = posts.filter(p => p.id !== confirmDelete.postId);
      const nm = myPosts.filter(id => id !== confirmDelete.postId);
      setMyPosts(nm); saveMyPosts(nm);
    } else {
      newPosts = posts.map(p =>
        p.id === confirmDelete.postId ? { ...p, replies: p.replies.filter(r => r.id !== confirmDelete.replyId) } : p
      );
      const nm = myPosts.filter(id => id !== confirmDelete.replyId);
      setMyPosts(nm); saveMyPosts(nm);
    }
    setPosts(newPosts); savePosts(newPosts);
    setConfirmDelete(null);
  };

  const isPro = currentUser?.plan === 'pro';

  return (
    <div style={s.page}>
      <SEO
        title="KnowFi Community — Money Wins, Questions & Tips"
        description="Real people sharing money wins, asking questions, and giving financial tips. No judgment. Join the KnowFi community."
        path="/community"
      />
      <div style={s.inner}>

        {/* Header */}
        <div style={s.header}>
          <div style={s.eyebrow}>💬 Community Forum</div>
          <h1 style={s.title}>Money talk, no judgment</h1>
          <p style={s.sub}>Ask questions, share wins, swap tips. Free and Pro members welcome.</p>
          <div style={s.headerActions}>
            <button style={s.newPostBtn} onClick={openPostModal}>+ New Post</button>
            {currentUser && (
              <span style={s.loggedInAs}>
                Posting as <strong>{currentUser.name || currentUser.email}</strong>
                {isPro && <ProBadge />}
              </span>
            )}
          </div>
        </div>

        {/* Category tabs */}
        <div style={s.tabsBar}>
          <div style={s.tabs}>
            {CATEGORIES.map(cat => (
              <button key={cat.id}
                style={{ ...s.tab, ...(activeTab === cat.id ? s.tabActive : {}) }}
                onClick={() => setActiveTab(cat.id)}>
                {cat.emoji} {cat.label}
              </button>
            ))}
          </div>
          <span style={s.postCount}>{filtered.length} post{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Post list */}
        <div style={s.postList}>
          {filtered.length === 0 && (
            <div style={s.emptyBox}>
              <div style={{ fontSize: '36px', marginBottom: '10px' }}>🌱</div>
              <div style={{ fontSize: '17px', fontWeight: '700', color: '#94a3b8', marginBottom: '6px' }}>Nothing here yet</div>
              <p style={{ color: '#475569', fontSize: '14px', marginBottom: '18px' }}>Be the first to post in this category.</p>
              <button style={s.newPostBtn} onClick={openPostModal}>+ Start the conversation</button>
            </div>
          )}

          {filtered.map(post => {
            const cs = CAT_STYLES[post.category] || CAT_STYLES.questions;
            const voted = !!votes[post.id];
            const isReplying = replyOpen === post.id;
            const replies = post.replies || [];
            const isMyPost = myPosts.includes(post.id);

            return (
              <div key={post.id} style={s.postCard}>

                {/* Top row: category + amount + time */}
                <div style={s.postTop}>
                  <span style={{ ...s.catBadge, background: cs.bg, color: cs.color, borderColor: cs.border }}>
                    {CATEGORIES.find(c => c.id === post.category)?.emoji}{' '}
                    {CATEGORIES.find(c => c.id === post.category)?.label}
                  </span>
                  {post.amount && <span style={s.amountBadge}>{post.amount}</span>}
                  <span style={s.timeAgo}>{formatTimeAgo(post.timestamp)}</span>
                </div>

                {/* Post body */}
                <p style={s.postText}>{post.text}</p>

                {/* Author + action buttons */}
                <div style={s.postFooter}>
                  <div style={s.authorRow}>
                    <div style={s.avatarCircle}>
                      {post.anonymous ? '🦆' : (post.authorName?.[0]?.toUpperCase() || 'K')}
                    </div>
                    <span style={s.authorName}>
                      {post.authorName || (post.anonymous ? 'Anonymous 🦆' : 'KnowFi Member')}
                    </span>
                    {!post.anonymous && post.isPro && <ProBadge />}
                  </div>

                  <div style={s.actionBtns}>
                    {/* Like button */}
                    <button
                      style={{ ...s.actionBtn, ...(voted ? s.actionBtnLiked : {}) }}
                      onClick={() => handleUpvote(post.id, false, null)}

                      title={voted ? 'Unlike' : 'Like this post'}
                    >
                      👍 <span style={s.actionLabel}>{voted ? 'Liked' : 'Like'}</span> {post.upvotes > 0 && <span style={s.actionCount}>{post.upvotes}</span>}
                    </button>

                    {/* Reply button */}
                    <button
                      style={{ ...s.actionBtn, ...(isReplying ? s.actionBtnActive : {}) }}
                      onClick={() => toggleReply(post.id)}
                    >
                      💬 <span style={s.actionLabel}>Reply</span>
                      {replies.length > 0 && <span style={s.actionCount}>{replies.length}</span>}
                    </button>

                    {/* Delete (own posts) */}
                    {isMyPost && (
                      <button style={s.deleteBtn}
                        onClick={() => setConfirmDelete({ type: 'post', postId: post.id })}
                        title="Delete your post">🗑</button>
                    )}
                  </div>
                </div>

                {/* Replies thread */}
                {replies.length > 0 && (
                  <div style={s.repliesThread}>
                    {replies.map(r => {
                      const rKey = `reply-${r.id}`;
                      const rVoted = !!votes[rKey];
                      const isMyReply = myPosts.includes(r.id);
                      return (
                        <div key={r.id} style={s.replyRow}>
                          <div style={s.replyConnector} />
                          <div style={s.replyContent}>
                            <div style={s.replyAuthorRow}>
                              <div style={{ ...s.avatarCircle, width: '26px', height: '26px', fontSize: '11px' }}>
                                {r.anonymous ? '🦆' : (r.authorName?.[0]?.toUpperCase() || 'K')}
                              </div>
                              <span style={s.replyAuthorName}>
                                {r.authorName || (r.anonymous ? 'Anonymous 🦆' : 'KnowFi Member')}
                              </span>
                              {!r.anonymous && r.isPro && <ProBadge />}
                              <span style={s.replyTime}>{formatTimeAgo(r.timestamp)}</span>
                            </div>
                            <p style={s.replyText}>{r.text}</p>
                            <div style={s.replyActions}>
                              <button
                                style={{ ...s.replyLikeBtn, ...(rVoted ? s.replyLikeBtnActive : {}) }}
                                onClick={() => handleUpvote(r.id, true, post.id)}

                              >
                                👍 {rVoted ? 'Liked' : 'Like'} {(r.upvotes || 0) > 0 && `· ${r.upvotes}`}
                              </button>
                              {isMyReply && (
                                <button style={s.deleteBtn}
                                  onClick={() => setConfirmDelete({ type: 'reply', postId: post.id, replyId: r.id })}>
                                  🗑
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Inline reply form */}
                {isReplying && (
                  <div style={s.replyForm}>
                    <textarea
                      style={s.replyTextarea}
                      placeholder="Write your reply..."
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      rows={3}
                      autoFocus
                    />
                    <div style={s.replyFormRow}>
                      <label style={s.anonLabel}>
                        <input type="checkbox" checked={replyAnon}
                          onChange={e => setReplyAnon(e.target.checked)}
                          style={{ accentColor: '#38bdf8', marginRight: '6px' }} />
                        Post anonymously 🦆
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {replyError && <span style={s.errMsg}>{replyError}</span>}
                        <button style={s.cancelSmall} onClick={() => toggleReply(post.id)}>Cancel</button>
                        <button style={s.submitSmall} onClick={() => handleReplySubmit(post.id)}>Post Reply →</button>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            );
          })}
        </div>

      </div>

      {/* ── Email gate modal ── */}
      {emailStep && (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) { setEmailStep(false); setEmailDraft(''); setEmailError(''); } }}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>Join the community 👋</h2>
              <button style={s.modalClose} onClick={() => { setEmailStep(false); setEmailDraft(''); setEmailError(''); }}>✕</button>
            </div>
            <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: 1.6, marginBottom: '20px' }}>
              Enter your email to post. You'll also get the free <strong style={{ color: '#f1f5f9' }}>KnowFi Brief</strong> — plain-English money news. No spam.
            </p>
            <form onSubmit={handleEmailGate}>
              <input style={{ ...s.input, marginBottom: '10px' }} type="email" placeholder="you@email.com"
                value={emailDraft} onChange={e => { setEmailDraft(e.target.value); setEmailError(''); }} autoFocus />
              {emailError && <p style={s.errMsg}>{emailError}</p>}
              <button type="submit" style={s.submitBtn}>Join Community →</button>
            </form>
            <p style={{ color: '#334155', fontSize: '11px', textAlign: 'center', marginTop: '10px' }}>No spam. Unsubscribe anytime.</p>
          </div>
        </div>
      )}

      {/* ── Delete confirmation ── */}
      {confirmDelete && (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) setConfirmDelete(null); }}>
          <div style={{ ...s.modal, maxWidth: '360px' }}>
            <h2 style={{ ...s.modalTitle, marginBottom: '12px' }}>Delete this {confirmDelete.type}?</h2>
            <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px', lineHeight: 1.6 }}>This can't be undone.</p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={s.cancelBtn} onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button style={s.deleteConfirmBtn} onClick={handleDelete}>Yes, delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── New post modal ── */}
      {showModal && (
        <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>New Post</h2>
              <button style={s.modalClose} onClick={() => setShowModal(false)}>✕</button>
            </div>

            {isPro && !form.anonymous && (
              <div style={s.proBanner}>
                ⭐ Your <strong>PRO</strong> badge will appear on this post
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={s.formGroup}>
                <label style={s.formLabel}>Category</label>
                <select style={s.select} value={form.category}
                  onChange={e => setForm({ ...form, category: e.target.value })}>
                  <option value="wins">🎉 Money Win</option>
                  <option value="questions">❓ Question</option>
                  <option value="tips">💡 Tip</option>
                  <option value="check">📊 Check My Plan</option>
                </select>
              </div>

              <div style={s.formGroup}>
                <label style={s.formLabel}>Your post</label>
                <textarea style={s.textarea}
                  placeholder="Share a win, ask a question, give a tip..."
                  value={form.text}
                  onChange={e => setForm({ ...form, text: e.target.value })}
                  rows={4} />
                <div style={{ color: form.text.length < MIN_POST_CHARS ? '#475569' : '#4ade80', fontSize: '11px', textAlign: 'right', marginTop: '4px' }}>
                  {form.text.length}/{MIN_POST_CHARS} min chars
                </div>
              </div>

              {form.category === 'wins' && (
                <div style={s.formGroup}>
                  <label style={s.formLabel}>Amount (optional — e.g. "$5k paid off")</label>
                  <input style={s.input} type="text" placeholder="e.g. $5,000"
                    value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
                </div>
              )}

              <div style={s.checkRow}>
                <input type="checkbox" id="anon" checked={form.anonymous}
                  onChange={e => setForm({ ...form, anonymous: e.target.checked })}
                  style={{ accentColor: '#38bdf8' }} />
                <label htmlFor="anon" style={s.checkLabel}>
                  Post anonymously 🦆{isPro ? ' (hides Pro badge)' : ''}
                </label>
              </div>

              {submitError && <p style={s.errMsg}>{submitError}</p>}
              <button type="submit" style={s.submitBtn}>Post to Community →</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page:  { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '48px 20px 80px', fontFamily: 'inherit' },
  inner: { maxWidth: '740px', margin: '0 auto' },

  // Header
  header:  { marginBottom: '28px' },
  eyebrow: { display: 'inline-block', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.2)', color: '#38bdf8', padding: '4px 13px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', marginBottom: '10px' },
  title:   { fontSize: 'clamp(22px, 3.5vw, 34px)', fontWeight: '900', margin: '0 0 6px', letterSpacing: '-0.5px' },
  sub:     { color: '#64748b', fontSize: '15px', margin: '0 0 18px', lineHeight: 1.6 },
  headerActions: { display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' },
  newPostBtn: { background: 'linear-gradient(135deg, #38bdf8, #a78bfa)', color: '#0f172a', border: 'none', borderRadius: '9px', padding: '9px 20px', fontSize: '14px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
  loggedInAs: { color: '#475569', fontSize: '13px' },

  // Tabs
  tabsBar:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', borderBottom: '1px solid #1e293b', paddingBottom: '14px' },
  tabs:     { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  tab:      { background: 'transparent', border: '1px solid #1e293b', color: '#475569', borderRadius: '20px', padding: '5px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' },
  tabActive: { background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.3)', color: '#38bdf8' },
  postCount: { color: '#334155', fontSize: '12px', whiteSpace: 'nowrap' },

  // Posts
  postList: { display: 'flex', flexDirection: 'column', gap: '0' },
  postCard: { borderBottom: '1px solid #1e293b', padding: '20px 4px', transition: 'background 0.1s' },

  postTop:  { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' },
  catBadge: { fontSize: '11px', fontWeight: '700', padding: '3px 9px', borderRadius: '20px', border: '1px solid', letterSpacing: '0.2px' },
  amountBadge: { background: 'rgba(34,197,94,0.1)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.2)', fontSize: '12px', fontWeight: '700', padding: '2px 8px', borderRadius: '6px' },
  timeAgo: { color: '#334155', fontSize: '12px', marginLeft: 'auto' },
  postText: { color: '#e2e8f0', fontSize: '16px', lineHeight: 1.7, margin: '0 0 14px' },

  postFooter:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' },
  authorRow:   { display: 'flex', alignItems: 'center', gap: '7px' },
  avatarCircle: { width: '30px', height: '30px', borderRadius: '50%', background: 'linear-gradient(135deg, #334155, #475569)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '800', color: '#f1f5f9', flexShrink: 0 },
  authorName:  { color: '#64748b', fontSize: '13px', fontWeight: '600' },

  actionBtns: { display: 'flex', alignItems: 'center', gap: '6px' },
  actionBtn:  { display: 'flex', alignItems: 'center', gap: '5px', background: '#1e293b', border: '1px solid #334155', color: '#64748b', borderRadius: '8px', padding: '6px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' },
  actionBtnLiked:  { background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.3)', color: '#38bdf8', cursor: 'default' },
  actionBtnActive: { background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.3)', color: '#a78bfa' },
  actionLabel: { display: 'inline' },
  actionCount: { background: '#334155', color: '#94a3b8', borderRadius: '4px', padding: '1px 5px', fontSize: '11px', fontWeight: '700' },
  deleteBtn:   { background: 'transparent', border: 'none', color: '#334155', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit', padding: '5px', transition: 'color 0.15s' },

  // Reply thread
  repliesThread: { marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '4px' },
  replyRow:    { display: 'flex', gap: '10px', alignItems: 'flex-start' },
  replyConnector: { width: '2px', background: '#1e293b', borderRadius: '2px', flexShrink: 0, alignSelf: 'stretch', minHeight: '100%', marginTop: '4px' },
  replyContent:   { flex: 1, background: '#111827', borderRadius: '10px', padding: '12px 14px' },
  replyAuthorRow: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', flexWrap: 'wrap' },
  replyAuthorName: { color: '#475569', fontSize: '12px', fontWeight: '700' },
  replyTime:      { color: '#1e3a5f', fontSize: '11px' },
  replyText:      { color: '#cbd5e1', fontSize: '14px', lineHeight: 1.65, margin: '0 0 8px' },
  replyActions:   { display: 'flex', alignItems: 'center', gap: '8px' },
  replyLikeBtn:   { background: 'transparent', border: '1px solid #1e293b', color: '#475569', borderRadius: '6px', padding: '3px 10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' },
  replyLikeBtnActive: { background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.3)', color: '#38bdf8', cursor: 'default' },

  // Reply form
  replyForm:    { marginTop: '12px', background: '#111827', borderRadius: '10px', padding: '14px', border: '1px solid #1e293b' },
  replyTextarea: { width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', color: '#f1f5f9', padding: '10px 12px', fontSize: '14px', fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: '10px' },
  replyFormRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' },
  anonLabel:    { display: 'flex', alignItems: 'center', color: '#64748b', fontSize: '13px', cursor: 'pointer' },
  cancelSmall:  { background: 'transparent', border: '1px solid #334155', color: '#64748b', borderRadius: '7px', padding: '6px 12px', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' },
  submitSmall:  { background: '#38bdf8', color: '#0f172a', border: 'none', borderRadius: '7px', padding: '6px 14px', fontSize: '13px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },

  emptyBox: { textAlign: 'center', padding: '60px 20px' },

  // Modals
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '20px' },
  modal:   { background: '#1e293b', borderRadius: '14px', padding: '28px', maxWidth: '480px', width: '100%', border: '1px solid #334155', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', maxHeight: '90vh', overflowY: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  modalTitle:  { fontSize: '20px', fontWeight: '800', margin: 0 },
  modalClose:  { background: 'transparent', border: 'none', color: '#64748b', fontSize: '18px', cursor: 'pointer', fontFamily: 'inherit', padding: '4px 8px' },

  proBanner: { background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: '8px', padding: '8px 12px', marginBottom: '16px', fontSize: '13px', color: '#f59e0b' },

  formGroup: { marginBottom: '16px' },
  formLabel: { display: 'block', color: '#94a3b8', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' },
  select:    { width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '9px', color: '#f1f5f9', padding: '11px 14px', fontSize: '14px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' },
  textarea:  { width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '9px', color: '#f1f5f9', padding: '11px 14px', fontSize: '14px', fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' },
  input:     { width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '9px', color: '#f1f5f9', padding: '11px 14px', fontSize: '14px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' },
  checkRow:  { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '18px' },
  checkLabel: { color: '#94a3b8', fontSize: '13px', cursor: 'pointer', lineHeight: 1.5 },
  errMsg:    { color: '#f87171', fontSize: '13px', margin: '0 0 10px' },
  submitBtn: { width: '100%', background: 'linear-gradient(135deg, #38bdf8, #a78bfa)', color: '#0f172a', border: 'none', borderRadius: '10px', padding: '13px', fontSize: '15px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },

  cancelBtn:       { flex: 1, background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: '10px', padding: '11px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  deleteConfirmBtn: { flex: 1, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '10px', padding: '11px', fontSize: '14px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
};
